// BL-1841 — a `tailwind-merge` instance that knows the admin type scale.
//
// ## The bug this exists to prevent
//
// `tailwind-merge` resolves conflicting utilities by classifying each class
// into a group and keeping the last one. It ships with Tailwind's DEFAULT
// theme, so it recognises `text-sm` as a font size and `text-white` as a
// colour — two groups, no conflict, both survive.
//
// It has never heard of `text-body`. Faced with `text-<unknown>` it falls back
// to classifying the class as a text COLOUR, which puts it in the same group as
// `text-white`. `Button` composes its classes variant-first, size-second:
//
//     twMerge('bg-primary text-white', 'px-4 py-2 text-body')
//
// so the size class arrives last, wins the group, and `text-white` is dropped.
// Every filled variant — primary, destructive, warning, ondark — lost its
// label colour and fell back to inherited ink. Nothing errors, nothing logs;
// the buttons simply render dark text on a dark fill.
//
// ## The fix, and why it is derived rather than listed
//
// The extension below teaches the font-size group every step name. It is
// generated from `adminType` — the same token source `tailwind.config.ts`
// binds and `generated-typography.css` renders — so a step added there cannot
// be forgotten here. A hand-written list would be a fourth place to keep in
// sync, and the failure mode of forgetting is invisible.
//
// Use `cn` anywhere admin classes are merged with `tailwind-merge`. Plain
// `clsx` callers are unaffected: clsx concatenates and never resolves
// conflicts, so it cannot hit this.

import { adminType } from '@auctogon/ui/tokens';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The scale's step names as Tailwind writes them: `bodyLg` → `body-lg`.
 *
 * Mirrors the key transform in `adminTypeRoles` (`packages/ui/.../css.ts`) and
 * the `fontSize` keys in admin-web's `tailwind.config.ts`. All three read the
 * same object, so they move together.
 */
const TYPE_STEP_NAMES = Object.keys(adminType).map((key) =>
    key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`),
);

/**
 * `twMerge`, taught the admin type scale.
 *
 * @sideEffect None — pure class-string resolution.
 */
export const cn = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [{ text: TYPE_STEP_NAMES }],
        },
    },
});
